// Modules
import moment from "moment";

// Interfaces
import {ContractUsage} from "Interfaces/AYPResultInterface";

enum SortType {
  CONTRACT_SORT = "contract_sort",
  DATE_SORT = "date_sort",
  USAGE_TYPE_SORT = "usage_type_sort",
  USAGE_ORDER= "usage_order"
};

const SORT_FIELDS = {
  [SortType.CONTRACT_SORT]: "contractId",
  [SortType.DATE_SORT]: "endDate",
  [SortType.USAGE_TYPE_SORT]: "usageType",
  [SortType.USAGE_ORDER]: "usageOrder",
}

enum SortDirection {
  ASC = 'ascending',
  DESC = 'descending',
}

/**
 * Evaluate a sort for two contracts based on end date DESCENDING
 */
function evalEndDateSortDesc(endDateA: string, endDateB: string) {
  const firstDate = moment(endDateA);
  const secondDate = moment(endDateB);

  const dateCompare = secondDate.valueOf() - firstDate.valueOf();

  // End Date sort was successful
  if (dateCompare > 0) {
    return 1;
  } else if (dateCompare < 0){
    return -1;
  }

  // End Dates are identical
  return 0;
}

/**
 * Evaluate a sort for two contracts based on end date ASCENDING
 */
function evalEndDateSortAsc(endDateA: string, endDateB: string) {
  const firstDate = moment(endDateA);
  const secondDate = moment(endDateB);

  const dateCompare = firstDate.valueOf() - secondDate.valueOf();

  // End Date sort was successful
  if (dateCompare > 0) {
    return 1;
  } else if (dateCompare < 0){
    return -1;
  }

  // End Dates are identical
  return 0;
}

/**
 * Evaluate a sort for two contracts based on Usage Type ASCENDING
 */
function evalUsageTypeSortAsc(usageTypeA: string, usageTypeB: string) {
  const letterCompare = (usageTypeA).localeCompare(usageTypeB);

  // Sort by letters in Usage Type was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Usage Types are identical
  return 0;
}

/**
 * Evaluate a sort for two contracts based on contractID DESCENDING
 */
function evalContractIdSortDesc(contractIdA: string, contractIdB: string) {
  // Sort by letters in contractID first
  const firstContractLetters = contractIdA.replace(/\d+/g, '');
  const secondContractLetters = contractIdB.replace(/\d+/g, '');

  const letterCompare = (secondContractLetters).localeCompare(firstContractLetters);

  // Sort by letters in contractID was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Sort by numbers in contractID second
  const firstContractNums = contractIdA.replace(/\D/g, '');
  const secondContractNums = contractIdB.replace(/\D/g, '');

  // Sort by numbers in contractID was successful
  if (firstContractNums < secondContractNums) {
      return 1;
  } else if (firstContractNums > secondContractNums) {
      return -1;
  }

  // ContractID's are identical
  return 0;
}

/**
 * Evaluate a sort for two contracts based on contractID ASCENDING
 */
function evalContractIdSortAsc(contractIdA: string, contractIdB: string) {
  // Sort by numbers in contractID first
  const firstContractNums = contractIdA.replace(/\D/g, '');
  const secondContractNums = contractIdB.replace(/\D/g, '');

  // Sort by numbers in contractID was successful
  if (firstContractNums > secondContractNums) {
      return 1;
  } else if (firstContractNums < secondContractNums) {
      return -1;
  }

  // Sort by letters in contractID second
  const firstContractLetters = contractIdA.replace(/\d+/g, '');
  const secondContractLetters = contractIdB.replace(/\d+/g, '');

  const letterCompare = (firstContractLetters).localeCompare(secondContractLetters);

  // Sort by letters in contractID was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // ContractID's are identical
  return 0;
}

function evalUsageOrderSortDesc(usageOrderA: string, usageOrderB: string) {
  // Sort by letters in contractID first
  const firstContractLetters = usageOrderA.replace(/\d+/g, '');
  const secondContractLetters = usageOrderB.replace(/\d+/g, '');

  const letterCompare = (secondContractLetters).localeCompare(firstContractLetters);

  // Sort by letters in contractID was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // Sort by numbers in contractID second
  const firstContractNums = usageOrderA.replace(/\D/g, '');
  const secondContractNums = usageOrderB.replace(/\D/g, '');

  // Sort by numbers in contractID was successful
  if (firstContractNums < secondContractNums) {
      return 1;
  } else if (firstContractNums > secondContractNums) {
      return -1;
  }

  // ContractID's are identical
  return 0;
}

function evalUsageOrderSortAsc(usageOrderA: string, usageOrderB: string) {
  const firstContractNums = usageOrderA.replace(/\D/g, '');
  const secondContractNums = usageOrderB.replace(/\D/g, '');

  // Sort by numbers in contractID was successful
  if (firstContractNums > secondContractNums) {
      return 1;
  } else if (firstContractNums < secondContractNums) {
      return -1;
  }

  // Sort by letters in contractID second
  const firstContractLetters = usageOrderA.replace(/\d+/g, '');
  const secondContractLetters = usageOrderB.replace(/\d+/g, '');

  const letterCompare = (firstContractLetters).localeCompare(secondContractLetters);

  // Sort by letters in contractID was successful
  if (letterCompare > 0) {
    return 1;
  } else if (letterCompare < 0) {
    return -1;
  }

  // ContractID's are identical
  return 0;
}

/**
 * Sort function to sort contracts based on EndDate DESCENDING
 *
 * The sort priorities are the following:
 *  1. End Date DESC (later to earlier)
 *  2. Numbers in contractID ASC (0-9)
 *  3. Letters in contractID ASC (A-Z)
 *  4. Usage Type ASC (A-Z)
 */
function endDateSortDescFunc(a: any, b:any) {
  // Try to sort by End Date
  const dateSortResult = evalEndDateSortDesc(
    a[SORT_FIELDS[SortType.DATE_SORT]],
    b[SORT_FIELDS[SortType.DATE_SORT]]
  );

  // End Date sort was successful
  if (dateSortResult > 0) {
    return 1;
  } else if (dateSortResult < 0) {
    return -1;
  }

  // Try to sort by contractID
  const contractSortResult = evalContractIdSortAsc(
    a[SORT_FIELDS[SortType.CONTRACT_SORT]],
    b[SORT_FIELDS[SortType.CONTRACT_SORT]]
  );

  // ContractID sort was successful
  if (contractSortResult > 0) {
    return 1;
  } else if (contractSortResult < 0) {
    return -1;
  }

  // Try to sort by Usage Type
  const usageTypeSort = evalUsageTypeSortAsc(
    a[SORT_FIELDS[SortType.USAGE_TYPE_SORT]],
    b[SORT_FIELDS[SortType.USAGE_TYPE_SORT]]
  );

  // Usage Type sort was successful
  if (usageTypeSort > 0) {
    return 1;
  } else if (usageTypeSort < 0) {
    return -1;
  }

  // Contract's ID, End Date, and Usage Type are identical
  return 0;
}

/**
 * Sort function to sort contracts based on EndDate ASCENDING
 *
 * The sort priorities are the following:
 *  1. End Date ASC (earlier to later)
 *  2. Numbers in contractID ASC (0-9)
 *  3. Letters in contractID ASC (A-Z)
 *  4. Usage Type ASC (A-Z)
 */
function endDateSortAscFunc(a: any, b:any) {
  // Try to sort by End Date
  const dateSortResult = evalEndDateSortAsc(
    a[SORT_FIELDS[SortType.DATE_SORT]],
    b[SORT_FIELDS[SortType.DATE_SORT]]
  );

  // End Date sort was successful
  if (dateSortResult > 0) {
    return 1;
  } else if (dateSortResult < 0) {
    return -1;
  }

  // Try to sort by contractID
  const contractSortResult = evalContractIdSortAsc(
    a[SORT_FIELDS[SortType.CONTRACT_SORT]],
    b[SORT_FIELDS[SortType.CONTRACT_SORT]]
  );

  // ContractID sort was successful
  if (contractSortResult > 0) {
    return 1;
  } else if (contractSortResult < 0) {
    return -1;
  }

  // Try to sort by Usage Type
  const usageTypeSort = evalUsageTypeSortAsc(
    a[SORT_FIELDS[SortType.USAGE_TYPE_SORT]],
    b[SORT_FIELDS[SortType.USAGE_TYPE_SORT]]
  );

  // Usage Type sort was successful
  if (usageTypeSort > 0) {
    return 1;
  } else if (usageTypeSort < 0) {
    return -1;
  }

  // Contract's ID, End Date, and Usage Type are identical
  return 0;
}

/**
 * Sort function to sort contracts based on contractID ASCENDING
 *
 * The sort priorities are the following:
 *  1. Numbers in contractID ASC (0-9)
 *  2. Letters in contractID ASC (A-Z)
 *  3. End Date ASC (earlier to later)
 *  4. Usage Type ASC (A-Z)
 */
function contractIdSortAscFunc(a: any, b:any) {
  // Try to sort by contractID
  const contractSortResult = evalContractIdSortAsc(
    a[SORT_FIELDS[SortType.CONTRACT_SORT]],
    b[SORT_FIELDS[SortType.CONTRACT_SORT]]
  );

  // ContractID sort was successful
  if (contractSortResult > 0) {
    return 1;
  } else if (contractSortResult < 0) {
    return -1;
  }

  // Try to sort by End Date
  const dateSortResult = evalEndDateSortAsc(
    a[SORT_FIELDS[SortType.DATE_SORT]],
    b[SORT_FIELDS[SortType.DATE_SORT]]
  );

  // End Date sort was successful
  if (dateSortResult > 0) {
    return 1;
  } else if (dateSortResult < 0) {
    return -1;
  }

  // Try to sort by Usage Type
  const usageTypeSort = evalUsageTypeSortAsc(
    a[SORT_FIELDS[SortType.USAGE_TYPE_SORT]],
    b[SORT_FIELDS[SortType.USAGE_TYPE_SORT]]
  );

  // Usage Type sort was successful
  if (usageTypeSort > 0) {
    return 1;
  } else if (usageTypeSort < 0) {
    return -1;
  }

  // Contract's ID, End Date, and Usage Type are identical
  return 0;
}

/**
 * Sort function to sort contracts based on contractID DESCENDING
 *
 * The sort priorities are the following:
 *  1. Letters in contractID DESC (Z-A)
 *  2. Numbers in contractID DESC (9-0)
 *  3. End Date ASC (earlier to later)
 *  4. Usage Type ASC (A-Z)
 */
function contractIdSortDescFunc(a: any, b:any) {
  // Try to sort by contractID
  const contractSortResult = evalContractIdSortDesc(
    a[SORT_FIELDS[SortType.CONTRACT_SORT]],
    b[SORT_FIELDS[SortType.CONTRACT_SORT]]
  );

  // ContractID sort was successful
  if (contractSortResult > 0) {
    return 1;
  } else if (contractSortResult < 0) {
    return -1;
  }

  // Try to sort by End Date
  const dateSortResult = evalEndDateSortAsc(
    a[SORT_FIELDS[SortType.DATE_SORT]],
    b[SORT_FIELDS[SortType.DATE_SORT]]
  );

  // End Date sort was successful
  if (dateSortResult > 0) {
    return 1;
  } else if (dateSortResult < 0) {
    return -1;
  }

  // Try to sort by Usage Type
  const usageTypeSort = evalUsageTypeSortAsc(
    a[SORT_FIELDS[SortType.USAGE_TYPE_SORT]],
    b[SORT_FIELDS[SortType.USAGE_TYPE_SORT]]
  );

  // Usage Type sort was successful
  if (usageTypeSort > 0) {
    return 1;
  } else if (usageTypeSort < 0) {
    return -1;
  }

  // Contract's ID, End Date, and Usage Type are identical
  return 0;
}

function usageOrderSortAscFunc(a: any, b:any) {

  const usageOrderSort = evalUsageOrderSortAsc(
    a[SORT_FIELDS[SortType.USAGE_ORDER]],
    b[SORT_FIELDS[SortType.USAGE_ORDER]]
  );

  if (usageOrderSort > 0) {
    return 1;
  } else if (usageOrderSort < 0) {
    return -1;
  }

  // Try to sort by contractID
  const contractSortResult = evalContractIdSortAsc(
    a[SORT_FIELDS[SortType.CONTRACT_SORT]],
    b[SORT_FIELDS[SortType.CONTRACT_SORT]]
  );

  // ContractID sort was successful
  if (contractSortResult > 0) {
    return 1;
  } else if (contractSortResult < 0) {
    return -1;
  }

  // Try to sort by End Date
  const dateSortResult = evalEndDateSortAsc(
    a[SORT_FIELDS[SortType.DATE_SORT]],
    b[SORT_FIELDS[SortType.DATE_SORT]]
  );

  // End Date sort was successful
  if (dateSortResult > 0) {
    return 1;
  } else if (dateSortResult < 0) {
    return -1;
  }  

  // Contract's ID, End Date, and Usage Type are identical
  return 0;
}

function usageOrderSortDescFunc(a: any, b:any) {
  const usageOrderSort = evalUsageOrderSortDesc(
    a[SORT_FIELDS[SortType.USAGE_ORDER]],
    b[SORT_FIELDS[SortType.USAGE_ORDER]]
  );

  // Usage Type sort was successful
  if (usageOrderSort > 0) {
    return 1;
  } else if (usageOrderSort < 0) {
    return -1;
  }
  // Try to sort by contractID
  const contractSortResult = evalContractIdSortAsc(
    a[SORT_FIELDS[SortType.CONTRACT_SORT]],
    b[SORT_FIELDS[SortType.CONTRACT_SORT]]
  );

  // ContractID sort was successful
  if (contractSortResult > 0) {
    return 1;
  } else if (contractSortResult < 0) {
    return -1;
  }

  // Try to sort by End Date
  const dateSortResult = evalEndDateSortAsc(
    a[SORT_FIELDS[SortType.DATE_SORT]],
    b[SORT_FIELDS[SortType.DATE_SORT]]
  );

  // End Date sort was successful
  if (dateSortResult > 0) {
    return 1;
  } else if (dateSortResult < 0) {
    return -1;
  }

  // Try to sort by Usage Type
  

  // Contract's ID, End Date, and Usage Type are identical
  return 0;
}

/**
 * sort contracts
 *
 * This is fired when the contracts are first loaded, and when a column sort
 * icon is clicked.
 *
 * If a sort icon was clicked, the switch logic is used for the sort field
 * that was clicked. If it's the initial load, sortField and
 * sortDirection will not be passed as params, and the default case will be
 * used in the switch.
 */
function sortContracts(
  contracts: ContractUsage[],
  sortField?: SortType,
  sortDirection?: SortDirection
): any[] {
  let sortFunction:any
  switch (sortField) {
    // Sort by End Date
    case SortType.DATE_SORT:
      if (sortDirection === SortDirection.ASC) {
        // Sort by End Date ASC
        sortFunction = (a:any, b:any) => endDateSortAscFunc(a, b);
      } else {
        // Sort by End Date DESC
        sortFunction = (a:any, b:any) => endDateSortDescFunc(a, b);
      }
      break;

    // Sort by ContractID
    case SortType.CONTRACT_SORT:
      if (sortDirection === SortDirection.ASC) {
        // Sort by ContractID ASC
        sortFunction = (a:any, b:any) => contractIdSortAscFunc(a, b);
      } else {
        // Sort by ContractID DESC
        sortFunction = (a:any, b:any) => contractIdSortDescFunc(a, b);
      }
      break;

      case SortType.USAGE_ORDER:
      if (sortDirection === SortDirection.ASC) {
        sortFunction = (a:any, b:any) => usageOrderSortAscFunc(a, b);
      } else {
        sortFunction = (a:any, b:any) => usageOrderSortDescFunc(a, b);
      }
      break;

    // default sort
    default:
      sortFunction = (a:any, b:any) => contractIdSortAscFunc(a, b);
  }

  // Return the sorted contracts
  return [...contracts].sort(sortFunction);
}

export {
  sortContracts,
  SortType,
  SortDirection,
};
